/**
 * Integrations module (#120): cards for external services. Rendering is
 * generic over INTEGRATIONS; the Nextcloud card is live (#98) when the
 * integrations service exists (proxy mode), otherwise a placeholder.
 */

import template from './integrations.template.html?raw';
import type { AppModule } from '../../shell/module.types';
import type { TranslationParams } from '../../../i18n/ui-manager.types';
import type { ConfirmDialogOptions } from '../../../components/confirm-dialog';
import type {
  default as IntegrationsService,
  NextcloudStatus,
} from '../../../core/integrations-service';
import { nextcloudErrorText } from '../../../components/nextcloud-error-text';
import {
  nextcloudBadgeKey,
  renderNextcloudCard,
  type NextcloudCardView,
} from './nextcloud-card';

export interface IntegrationDescriptor {
  id: string;
  nameKey: string;
  descriptionKey: string;
  /** Static, trusted inline SVG. */
  icon: string;
  status: 'comingSoon';
}

const ICON_ATTRS =
  'class="integrations__icon" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"';

const STATUS_KEYS: Record<IntegrationDescriptor['status'], string> = {
  comingSoon: 'modules.comingSoon',
};

export const INTEGRATIONS: readonly IntegrationDescriptor[] = [
  {
    id: 'nextcloud',
    nameKey: 'modules.integrations.nextcloud.name',
    descriptionKey: 'modules.integrations.nextcloud.description',
    icon: `<svg ${ICON_ATTRS}><path d="M7 18a4 4 0 0 1-.6-7.96A5.5 5.5 0 0 1 17 8.5a4.75 4.75 0 0 1 .5 9.5z"/></svg>`,
    status: 'comingSoon',
  },
];

export interface IntegrationsModuleDeps {
  t: (key: string, params?: TranslationParams) => string;
  onLanguageChange: (callback: () => void) => void;
  enabled: boolean;
  /** #98: null outside proxy mode → the Nextcloud card stays a placeholder. */
  getService?: () => IntegrationsService | null;
  /** Connect / reconnect; resolves the new status, null when cancelled. */
  connectDialog?: (opts: {
    service: IntegrationsService;
  }) => Promise<NextcloudStatus | null>;
  /** Pick a folder to expose; resolves the new status, null when cancelled. */
  folderPicker?: (opts: {
    service: IntegrationsService;
  }) => Promise<NextcloudStatus | null>;
  confirm?: (options: ConfirmDialogOptions) => Promise<boolean>;
}

const K = 'modules.integrations.nextcloud';

export function createIntegrationsModule(
  deps: IntegrationsModuleDeps
): AppModule {
  const { t } = deps;
  let root: HTMLElement | null = null;
  const view: NextcloudCardView = {
    status: null,
    loading: false,
    message: null,
    failed: false,
  };
  let loadId = 0;

  const service = (): IntegrationsService | null => deps.getService?.() ?? null;

  const errorText = (error: unknown): string => nextcloudErrorText(error, t);

  const update = (patch: Partial<NextcloudCardView>): void => {
    Object.assign(view, patch);
    render();
  };

  const load = async (): Promise<void> => {
    const svc = service();
    if (!svc) return;
    const id = ++loadId;
    update({ loading: true, failed: false, message: null });
    let status: NextcloudStatus;
    try {
      status = await svc.status();
    } catch {
      if (id === loadId) update({ loading: false, failed: true, status: null });
      return;
    }
    if (id !== loadId) return;
    update({ loading: false, status });
    if (!status.canManage || status.state !== 'active') return;
    try {
      const checked = await svc.check();
      if (id === loadId) update({ status: checked });
    } catch (error) {
      if (id === loadId) update({ message: errorText(error) });
    }
  };

  /** Runs an action that answers the new status; errors go to the message line. */
  const run = async (
    action: () => Promise<NextcloudStatus | null>
  ): Promise<void> => {
    update({ message: null });
    try {
      const status = await action();
      if (status) update({ status });
    } catch (error) {
      update({ message: errorText(error) });
    }
  };

  const handlers = {
    connect: () => {
      const svc = service();
      if (svc && deps.connectDialog)
        void run(() => deps.connectDialog!({ service: svc }));
    },
    reconnect: () => handlers.connect(),
    confirm: () => {
      const svc = service();
      if (svc) void run(() => svc.confirm());
    },
    cancelPending: () => {
      const svc = service();
      if (svc) void run(() => svc.connectCancel());
    },
    addFolder: () => {
      const svc = service();
      if (svc && deps.folderPicker)
        void run(() => deps.folderPicker!({ service: svc }));
    },
    removeFolder: (folder: { id: string }) => {
      const svc = service();
      if (svc) void run(() => svc.removeFolder(folder.id));
    },
    disconnect: () => {
      const svc = service();
      if (!svc || !deps.confirm) return;
      void run(async () => {
        const yes = await deps.confirm!({
          title: t(`${K}.disconnect`),
          message: t(`${K}.disconnectConfirm`),
          confirmLabel: t(`${K}.disconnect`),
          cancelLabel: t(`${K}.cancel`),
          danger: true,
        });
        return yes ? svc.disconnect() : null;
      });
    },
    retry: () => void load(),
  };

  const render = (): void => {
    if (!root) return;
    const title = root.querySelector<HTMLElement>('[data-integrations-title]');
    if (title) title.textContent = t('modules.integrations.label');
    const grid = root.querySelector<HTMLElement>('[data-integrations-grid]');
    if (!grid) return;
    grid.replaceChildren();
    const live = service() !== null;
    for (const integration of INTEGRATIONS) {
      const item = document.createElement('li');
      item.className = 'integrations__card';
      item.dataset.integration = integration.id;

      const top = document.createElement('div');
      top.className = 'integrations__card-top';
      top.insertAdjacentHTML('beforeend', integration.icon);
      const badge = document.createElement('span');
      badge.className = 'integrations__badge';
      badge.dataset.integrationBadge = '';
      top.append(badge);

      const name = document.createElement('h2');
      name.className = 'integrations__name';
      name.textContent = t(integration.nameKey);

      const description = document.createElement('p');
      description.className = 'integrations__description';
      description.textContent = t(integration.descriptionKey);

      item.append(top, name, description);

      if (integration.id === 'nextcloud' && live) {
        badge.textContent = t(nextcloudBadgeKey(view));
        const body = document.createElement('div');
        body.className = 'integrations__body';
        body.dataset.nextcloudBody = '';
        renderNextcloudCard(body, view, t, handlers);
        item.append(body);
      } else {
        badge.textContent = t(STATUS_KEYS[integration.status]);
        const connect = document.createElement('button');
        connect.type = 'button';
        connect.className = 'btn btn-outline-secondary integrations__connect';
        connect.disabled = true;
        connect.textContent = t('modules.integrations.connect');
        item.append(connect);
      }
      grid.append(item);
    }
  };

  return {
    id: 'integrations',
    route: '/integrations',
    labelKey: 'modules.integrations.label',
    descriptionKey: 'modules.integrations.description',
    icon: 'integrations',
    enabled: deps.enabled,
    showOnDashboard: true,

    mount(outlet) {
      outlet.innerHTML = template;
      root = outlet;
      render();
      deps.onLanguageChange(() => {
        if (root) render();
      });
    },

    onEnter() {
      void load();
    },
  };
}
